import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useLabUpdateAPI } from "@/app/hooks/api_hooks/LabReport/useLabUpdateAPI";
import { useLabDetailsAPI } from "@/app/hooks/api_hooks/LabReport/useLabDetailsAPI";
import { useLabChangeReasonsAPI } from "@/app/hooks/api_hooks/LabReport/useLabChangeReasonsAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useForm } from "react-hook-form";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import { useLocation } from "react-router";
import { ILabReportListState } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ClipboardList, Info } from "lucide-react";
import clsx from "clsx";

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  uploadModalType: string | null;
  requisitionId?: string | null;
  testId?: string | null;
  testDetails?: any;
}

interface FormValues {
  lab_id: string;
  lab_change_reason: string;
}

const ChangeLabPartnerModal = ({ open, setOpen, uploadModalType, requisitionId, testId, testDetails }: Props) => {
  const [loading, setLoading] = useState(false);

  const { execute: labUpdate } = useLabUpdateAPI();

  const { execute: fetchLabReports } = useLabReportFilterListAPI();
  const fetchStatsList = useLabReportStatsListAPI();
  const location = useLocation();
  const { getRouteKey } = useRouter();
  const { labReportListFilter } = useRouteInfo(getRouteKey("LAB_REPORT", "id")).routeState.state as ILabReportListState;
  const { execute: fetchLabPartnerAsperRequisition, labDetails } = useLabDetailsAPI();

  const isRetestChild = !!testDetails?.parent_requisition_id;
  const allLabPartners = labDetails?.lab_partners || [];
  const filteredLabPartners = isRetestChild ? allLabPartners.filter((l: any) => !l?.is_vetic_lab) : allLabPartners;

  const labPartnerList = useSelectMenuReducer(filteredLabPartners, "lab_name", "lab_id");
  const { execute: fetchReasons, labChangeReasons } = useLabChangeReasonsAPI();
  const reasonOptions = labChangeReasons.map((reason) => ({ id: reason, title: reason }));
  // console.log("labPartnerList", labPartnerList);
  // console.log("labDetails",labDetails);

  const formHook = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      lab_id: "",
      lab_change_reason: "",
    },
  });

  const {
    formState: { isDirty },
  } = formHook;

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  useEffect(() => {
    if (requisitionId && testId) {
      fetchLabPartnerAsperRequisition(requisitionId, testId);
    }
  }, [requisitionId, testId]);

  const onSubmit = (data: FormValues) => {
    if (uploadModalType !== "EDIT") {
      setOpen(false);
      return;
    }
    setLoading(true);
    const constructedBody = {
      lab_id: data.lab_id,
      lab_change_reason: data.lab_change_reason,
    };
    labUpdate(requisitionId, constructedBody).then(({ status }) => {
      setLoading(false);
      if (status) {
        const type = location.pathname.includes("internal") ? 3 : location.pathname.includes("incoming") ? 2 : 1;
        const payload: any = {
          start_date: labReportListFilter?.start_date,
          end_date: labReportListFilter?.end_date,
          status_ids: labReportListFilter?.status_ids,
          clinic_ids: labReportListFilter?.clinic_ids,
          type,
        };
        fetchStatsList(payload);
        fetchLabReports(payload);
        formHook.reset();
        setOpen(false);
      }
    });
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => !loading && setOpen(false)}>
        <div className="flex min-h-screen items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-8">
          <Dialog.Panel className="w-full max-w-xl rounded-3xl bg-white shadow-2xl overflow-visible">
            {/* Header */}
            <div className="bg-[#F9FAFB] px-6 py-4 flex items-center justify-between border-b border-gray-100 rounded-t-3xl">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-black rounded-xl">
                  <ClipboardList className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900 leading-tight">Change Lab Partner</h2>
                  <p className="text-[11px] text-gray-500 font-medium  tracking-wider mt-0.5">Edit Requisition</p>
                </div>
              </div>
              <button
                type="button"
                className="p-1.5 rounded-full hover:bg-gray-200 transition-colors bg-gray-100"
                disabled={loading}
                onClick={() => setOpen(false)}
              >
                <XMarkIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* Standardized Detail Section */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 font-primary">
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Requisition ID</p>
                  <p className="text-xs font-medium text-gray-900">{testDetails?.requisition_number || "NA"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Customer Name</p>
                  <p className="text-xs font-medium text-gray-900">{testDetails?.customer_details?.name || "NA"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Pet Details</p>
                  <p className="text-xs font-medium text-gray-900">
                    {testDetails?.pet_details?.name || "NA"}
                    {testDetails?.pet_details?.pet_species_name && (
                      <span className="text-gray-400 font-normal ml-1">
                        ({testDetails?.pet_details?.pet_species_name?.toLowerCase() === "canine" ? "Dog" : "Cat"})
                      </span>
                    )}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Lab Partner</p>
                  <p className="text-xs font-medium text-gray-900 line-clamp-1" title={testDetails?.lab_partner_name}>
                    {testDetails?.lab_partner_name || "NA"}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500  ">Test Name</p>
                  <p className="text-xs  text-gray-900  " title={testDetails?.test_name || testDetails?.test_name}>
                    {testDetails?.test_name || testDetails?.test_name || "NA"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500  ">Doctor</p>
                  <p className="text-xs font-medium text-gray-900 " title={testDetails?.staff_details?.doctor_name}>
                    {testDetails?.staff_details?.doctor_name || "NA"}
                  </p>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-700  tracking-wider flex items-center gap-1.5 ml-1">
                    Select New Lab Partner <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelectMenu
                    errorMessages={[{ message: "", type: "required" }]}
                    fieldError={formHook.formState.errors?.lab_id}
                    register={formHook.register("lab_id", { required: true })}
                    placeholder="Search and select lab..."
                    showDropdownIcon
                    containerClassName="w-full"
                    selectItems={labPartnerList}
                    onSelectItem={(item) => {
                      if (!item) return;
                      formHook.setValue("lab_id", item.id, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-700  tracking-wider flex items-center gap-1.5 ml-1">
                    Reason for Change <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelectMenu
                    errorMessages={[{ message: "", type: "required" }]}
                    fieldError={formHook.formState.errors?.lab_change_reason}
                    register={formHook.register("lab_change_reason", { required: true })}
                    placeholder={labChangeReasons.length ? "Select reason" : "Loading reasons..."}
                    showDropdownIcon
                    containerClassName="w-full"
                    selectItems={reasonOptions}
                    onSelectItem={(item) => {
                      if (!item) return;
                      formHook.setValue("lab_change_reason", item.id, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  />
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 rounded-lg">
                    <Info className="w-3.5 h-3.5 text-amber-600" />
                    <p className="text-[10px] text-amber-700 font-medium italic">
                      This action will update the lab partner for current requisition.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100 rounded-b-3xl">
              <button
                type="button"
                disabled={loading}
                onClick={() => setOpen(false)}
                className="px-5 py-2.5 text-xs font-bold text-gray-600 hover:text-gray-900 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={formHook.handleSubmit(onSubmit)}
                className={clsx(
                  "rounded-xl px-8 py-2.5 text-xs font-bold text-white shadow-sm transition-all active:scale-95",
                  !loading ? "bg-black hover:bg-gray-800" : "bg-gray-400 cursor-not-allowed",
                )}
              >
                {loading ? "Updating..." : "Update Partner"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
export default ChangeLabPartnerModal;
